import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Shield, Users as UsersIcon, UserCog, Search, Building2, KeyRound, Code2 } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { PermissionsMatrix } from '../components/PermissionsMatrix';
import { API } from '@/lib/apiBase';

const ROLE_LABELS = {
  admin: { label: 'Administrateur', color: 'bg-primary text-black' },
  gestionnaire: { label: 'Gestionnaire', color: 'bg-teal-500 text-black' },
  utilisateur: { label: 'Utilisateur', color: 'bg-secondary text-foreground' },
  developpeur: { label: 'Développeur', color: 'bg-violet-500 text-white' },
};

const VALID_PRIMARY_ROLES = ['admin', 'gestionnaire', 'utilisateur', 'developpeur'];

export const Users = () => {
  const { token, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/external/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const handleRoleChange = async (userId, newRole) => {
    if (!isAdmin) {
      toast.error('Seuls les administrateurs peuvent modifier les rôles');
      return;
    }
    if (!newRole || !VALID_PRIMARY_ROLES.includes(newRole)) {
      return;
    }
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole }, { headers });
      toast.success('Rôle mis à jour');
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || d.message || JSON.stringify(d)).join(' — ')
            : null;
      toast.error(msg || 'Erreur lors de la mise à jour du rôle');
    }
  };

  const handleDeveloperToggle = async (userId, checked) => {
    if (!isAdmin) {
      toast.error('Seuls les administrateurs peuvent modifier ce rôle');
      return;
    }
    try {
      await axios.put(
        `${API}/users/${userId}/role`,
        { is_developer: Boolean(checked) },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      toast.success(checked ? 'Rôle Développeur activé' : 'Rôle Développeur retiré');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du rôle Développeur');
    }
  };

  const getInitials = (name, email) => {
    const str = name || email || 'U';
    return str.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getDisplayName = (user) => {
    return user.display_name || user.name || 
           (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : null) ||
           user.username || 
           user.email_principal?.split('@')[0] || 
           'Utilisateur';
  };

  const getEmail = (user) => {
    return user.email_principal || user.email_personal || user.email || '-';
  };

  const primaryRoleForDisplay = (role) =>
    VALID_PRIMARY_ROLES.includes(role) ? role : 'utilisateur';

  const filteredUsers = users.filter(user => {
    const name = getDisplayName(user).toLowerCase();
    const email = getEmail(user).toLowerCase();
    const username = (user.username || '').toLowerCase();
    const service = (user.service || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    
    return name.includes(search) || 
           email.includes(search) || 
           username.includes(search) ||
           service.includes(search);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <UsersIcon className="w-8 h-8 text-primary" />
            Utilisateurs
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestion des utilisateurs et des permissions
          </p>
        </div>
        <Badge variant="outline" className="border-primary text-primary">
          {users.length} utilisateur{users.length > 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Tabs for Users and Permissions */}
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-secondary/50 border border-border">
          <TabsTrigger 
            value="users" 
            className="data-[state=active]:bg-primary data-[state=active]:text-black"
            data-testid="tab-users"
          >
            <UserCog className="w-4 h-4 mr-2" />
            Utilisateurs
          </TabsTrigger>
          <TabsTrigger 
            value="permissions" 
            className="data-[state=active]:bg-primary data-[state=active]:text-black"
            data-testid="tab-permissions"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            Permissions
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          {/* Search */}
          <div className="relative max-w-md mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom, email, service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-secondary/50 border-border"
              data-testid="search-users"
            />
          </div>

          {/* Users Table */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground font-heading flex items-center gap-2">
                <UserCog className="w-5 h-5 text-primary" />
                Liste des utilisateurs
                {isAdmin && <span className="text-xs font-normal text-muted-foreground ml-2">(Admin: vous pouvez modifier les rôles)</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Utilisateur</TableHead>
                    <TableHead className="text-muted-foreground">Email</TableHead>
                    <TableHead className="text-muted-foreground">Service</TableHead>
                    <TableHead className="text-muted-foreground">Rôle Prysm'Analytics</TableHead>
                    <TableHead className="text-muted-foreground min-w-[200px]">
                      <span className="inline-flex items-center gap-1.5">
                        <Code2 className="w-4 h-4" />
                        Développeur
                      </span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow 
                      key={user.id} 
                      className="border-border table-row-hover"
                      data-testid={`user-row-${user.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-9 h-9 border border-border">
                            <AvatarImage src={user.photo} />
                            <AvatarFallback className="bg-primary/20 text-primary text-sm">
                              {getInitials(getDisplayName(user), getEmail(user))}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium text-foreground">
                            {getDisplayName(user)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {getEmail(user)}
                      </TableCell>
                      <TableCell>
                        {user.service ? (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            <span className="text-foreground">{user.service}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isAdmin ? (
                          <Select
                            value={primaryRoleForDisplay(user.role)}
                            onValueChange={(value) => handleRoleChange(user.id, value)}
                          >
                            <SelectTrigger 
                              className="w-44 bg-secondary/50 border-border"
                              data-testid={`role-select-${user.id}`}
                            >
                              <SelectValue>
                                <Badge className={ROLE_LABELS[primaryRoleForDisplay(user.role)]?.color}>
                                  {ROLE_LABELS[primaryRoleForDisplay(user.role)]?.label}
                                </Badge>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="admin">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-4 h-4 text-primary" />
                                  Administrateur
                                </div>
                              </SelectItem>
                              <SelectItem value="gestionnaire">
                                <div className="flex items-center gap-2">
                                  <UserCog className="w-4 h-4 text-teal-500" />
                                  Gestionnaire
                                </div>
                              </SelectItem>
                              <SelectItem value="utilisateur">
                                <div className="flex items-center gap-2">
                                  <UsersIcon className="w-4 h-4 text-muted-foreground" />
                                  Utilisateur
                                </div>
                              </SelectItem>
                              <SelectItem value="developpeur">
                                <div className="flex items-center gap-2">
                                  <Code2 className="w-4 h-4 text-violet-500" />
                                  Développeur
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={ROLE_LABELS[primaryRoleForDisplay(user.role)]?.color}>
                            {ROLE_LABELS[primaryRoleForDisplay(user.role)]?.label}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {isAdmin ? (
                          <div className="flex items-center gap-3">
                            <Switch
                              id={`dev-${user.id}`}
                              checked={Boolean(user.is_developer)}
                              onCheckedChange={(checked) => handleDeveloperToggle(user.id, checked)}
                              data-testid={`developer-switch-${user.id}`}
                            />
                            <Label
                              htmlFor={`dev-${user.id}`}
                              className="text-sm text-muted-foreground cursor-pointer"
                            >
                              {user.is_developer ? 'Oui' : 'Non'}
                            </Label>
                          </div>
                        ) : user.is_developer ? (
                          <Badge variant="outline" className="border-primary/50 text-primary gap-1">
                            <Code2 className="w-3.5 h-3.5" />
                            Développeur
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {filteredUsers.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  {searchTerm ? 'Aucun utilisateur trouvé pour cette recherche' : 'Aucun utilisateur dans la base'}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permissions Tab */}
        <TabsContent value="permissions" className="mt-6">
          <PermissionsMatrix />
        </TabsContent>
      </Tabs>
    </div>
  );
};
